import { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

/**
 * Animated ATS Score display with confetti celebration
 * The "Holy Shit" moment - score counter animates from 0 to final score
 * Now includes domain match indicator for career pivot scenarios
 */
const ATSScore = ({ score, previousScore = null, animate = true, size = 'lg', domainMatch = null }) => {
  const [displayScore, setDisplayScore] = useState(animate ? 0 : score);
  const [showConfetti, setShowConfetti] = useState(false);
  const [hasAnimated, setHasAnimated] = useState(false);
  const containerRef = useRef(null);

  // Size configurations
  const sizeConfig = {
    sm: { score: 'text-3xl', label: 'text-xs', container: 'w-24 h-24' },
    md: { score: 'text-4xl', label: 'text-sm', container: 'w-32 h-32' },
    lg: { score: 'text-5xl', label: 'text-base', container: 'w-40 h-40' },
    xl: { score: 'text-6xl', label: 'text-lg', container: 'w-48 h-48' }
  };

  const config = sizeConfig[size] || sizeConfig.lg;

  // Score color based on value
  const getScoreColor = (value) => {
    if (value >= 80) return 'text-green-600';
    if (value >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getScoreLabel = (value) => {
    if (value >= 90) return 'Excellent Match!';
    if (value >= 80) return 'Great Match!';
    if (value >= 70) return 'Good Match';
    if (value >= 60) return 'Fair Match';
    return 'Needs Improvement';
  };

  const getProgressColor = (value) => {
    if (value >= 80) return 'stroke-green-500';
    if (value >= 60) return 'stroke-yellow-500';
    return 'stroke-red-500';
  };

  // Animate score counter
  useEffect(() => {
    if (!animate || hasAnimated) {
      setDisplayScore(score);
      return;
    }

    const startScore = previousScore || 0;
    const duration = 1500; // 1.5 seconds
    const startTime = Date.now();

    const animateScore = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Easing function for smooth animation
      const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);
      const easedProgress = easeOutCubic(progress);

      const currentScore = Math.round(startScore + (score - startScore) * easedProgress);
      setDisplayScore(currentScore);

      if (progress < 1) {
        requestAnimationFrame(animateScore);
      } else {
        setHasAnimated(true);
        // Trigger confetti if score is good
        if (score >= 80) {
          setShowConfetti(true);
          setTimeout(() => setShowConfetti(false), 3000);
        }
      }
    };

    requestAnimationFrame(animateScore);
  }, [score, previousScore, animate, hasAnimated]);

  // Generate confetti particles
  const renderConfetti = () => {
    if (!showConfetti) return null;

    const particles = [];
    const colors = ['#10B981', '#3B82F6', '#8B5CF6', '#F59E0B', '#EC4899'];

    for (let i = 0; i < 50; i++) {
      const style = {
        left: `${Math.random() * 100}%`,
        animationDelay: `${Math.random() * 0.5}s`,
        animationDuration: `${1 + Math.random() * 2}s`,
        backgroundColor: colors[Math.floor(Math.random() * colors.length)]
      };
      particles.push(
        <div
          key={i}
          className="confetti-particle"
          style={style}
        />
      );
    }
    return particles;
  };

  // Calculate circle progress
  const radius = 45;
  const circumference = 2 * Math.PI * radius;
  const progress = (displayScore / 100) * circumference;
  const strokeDashoffset = circumference - progress;

  return (
    <div ref={containerRef} className="relative inline-flex flex-col items-center">
      {/* Confetti container */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {renderConfetti()}
      </div>

      {/* Score circle */}
      <div className={`relative ${config.container}`}>
        <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
          {/* Background circle */}
          <circle
            cx="50"
            cy="50"
            r={radius}
            fill="none"
            stroke="#E5E7EB"
            strokeWidth="8"
          />
          {/* Progress circle */}
          <circle
            cx="50"
            cy="50"
            r={radius}
            fill="none"
            className={`transition-all duration-300 ${getProgressColor(displayScore)}`}
            strokeWidth="8"
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            style={{
              transition: animate ? 'stroke-dashoffset 1.5s cubic-bezier(0.4, 0, 0.2, 1)' : 'none'
            }}
          />
        </svg>

        {/* Score number in center */}
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className={`font-bold ${config.score} ${getScoreColor(displayScore)}`}>
            {displayScore}
            <span className="text-2xl">%</span>
          </span>
        </div>
      </div>

      {/* Label */}
      <div className={`mt-2 text-center ${config.label}`}>
        <p className={`font-semibold ${getScoreColor(displayScore)}`}>
          {getScoreLabel(displayScore)}
        </p>
        <p className="text-gray-500 text-sm">ATS Compatibility</p>
      </div>

      {/* Score improvement indicator */}
      {previousScore !== null && previousScore < score && (
        <div className="mt-2 flex items-center gap-1 text-green-600 text-sm font-medium animate-bounce-subtle">
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M5.293 9.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L11 7.414V15a1 1 0 11-2 0V7.414L6.707 9.707a1 1 0 01-1.414 0z" clipRule="evenodd" />
          </svg>
          <span>+{score - previousScore}% improvement</span>
        </div>
      )}

      {/* Domain Match Indicator */}
      {domainMatch && domainMatch.level && (
        <div className={`mt-4 px-4 py-2 rounded-full text-sm font-medium ${
          domainMatch.level === 'Low'
            ? 'bg-red-100 text-red-700 border border-red-200'
            : domainMatch.level === 'Medium'
            ? 'bg-yellow-100 text-yellow-700 border border-yellow-200'
            : 'bg-green-100 text-green-700 border border-green-200'
        }`}>
          <span className="font-semibold">Domain Match: {domainMatch.level}</span>
          {domainMatch.level === 'Low' && (
            <span className="block text-xs font-normal mt-0.5">
              Career pivot detected
            </span>
          )}
          {domainMatch.level === 'Medium' && (
            <span className="block text-xs font-normal mt-0.5">
              Partial field match
            </span>
          )}
        </div>
      )}

      {/* CSS for confetti animation */}
      <style>{`
        .confetti-particle {
          position: absolute;
          width: 10px;
          height: 10px;
          border-radius: 2px;
          animation: confetti-fall linear forwards;
        }

        @keyframes confetti-fall {
          0% {
            transform: translateY(-100%) rotate(0deg);
            opacity: 1;
          }
          100% {
            transform: translateY(400%) rotate(720deg);
            opacity: 0;
          }
        }

        @keyframes bounce-subtle {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-3px);
          }
        }

        .animate-bounce-subtle {
          animation: bounce-subtle 1s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

ATSScore.propTypes = {
  score: PropTypes.number.isRequired,
  previousScore: PropTypes.number,
  animate: PropTypes.bool,
  size: PropTypes.oneOf(['sm', 'md', 'lg', 'xl']),
  domainMatch: PropTypes.shape({
    level: PropTypes.oneOf(['High', 'Medium', 'Low']),
    score: PropTypes.number,
    explanation: PropTypes.string
  })
};

export default ATSScore;
